import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { STATUS_LABEL } from "@/lib/constants";
import { RefreshCw, UserPlus, Pencil, AlertCircle, RotateCcw, CheckCircle2, Clock, Paperclip, ArrowRightLeft, LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "BARU", label: "Tambahan Baru", icon: UserPlus, color: "emerald" },
  { key: "UBAH", label: "Perubahan Data", icon: Pencil, color: "blue" },
  { key: "PINDAH_TPS", label: "Pindah TPS", icon: ArrowRightLeft, color: "purple" },
];

const STATUS_UI = {
  APPLIED: { label: "Diterapkan", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  REVERTED: { label: "Dikembalikan", color: "bg-red-100 text-red-800", icon: RotateCcw },
  PENDING: { label: "Menunggu Persetujuan", color: "bg-amber-100 text-amber-800", icon: Clock },
  APPROVED: { label: "Disetujui Panitia", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  REJECTED: { label: "Ditolak Panitia", color: "bg-red-100 text-red-800", icon: AlertCircle },
};

/**
 * "Perubahan Saya" — log semua CRUD yang PPDP lakukan (BARU / STATUS / UBAH).
 * Read-only untuk PPDP. Tombol "Kembalikan" hanya untuk Admin/Panitia (di menu Delta DPS).
 */
export default function MyChangesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("BARU");

  const load = async () => {
    setLoading(true);
    try {
      // UBAH tab juga tampilkan entri legacy tipe STATUS (semua bukan BARU)
      const tipeParam = tab === "UBAH" ? "UBAH,STATUS" : tab;
      // Admin/Panitia: untuk PINDAH_TPS, tampilkan SEMUA log (bukan hanya milik sendiri) agar bisa "Batalkan Pindah"
      const scopeMine = !(tab === "PINDAH_TPS" && (user?.role === "admin" || user?.role === "panitia"));
      const params = { tipe: tipeParam };
      if (scopeMine) params.mine = true;
      const { data } = await api.get("/voter-changes", { params });
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const counts = useMemo(() => {
    const c = { APPLIED: 0, REVERTED: 0, PENDING: 0, REJECTED: 0 };
    for (const i of items) {
      c[i.status] = (c[i.status] || 0) + 1;
    }
    return c;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="my-changes-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">
            {user?.role === "ppdp" ? "PPDP" : user?.role?.toUpperCase()}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Perubahan Saya</h1>
          <p className="text-slate-600 text-sm mt-2 max-w-2xl">
            {user?.role === "ppdp"
              ? <>Log semua perubahan data pemilih yang Anda lakukan. Jika suatu perubahan dikembalikan oleh Panitia, entry berikut akan bertanda <b>Dikembalikan</b>.</>
              : <>Log perubahan CRUD Anda. Untuk tab <b>Pindah TPS</b>, ditampilkan semua log pindah antar-TPS agar Anda bisa <b>Batalkan Pindah</b> jika diperlukan.</>}
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="btn-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </header>

      {/* Ringkasan */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { s: "APPLIED", label: "Diterapkan", color: "emerald" },
          { s: "REVERTED", label: "Dikembalikan", color: "red" },
          { s: "PENDING", label: "Menunggu", color: "amber" },
          { s: "REJECTED", label: "Ditolak", color: "red" },
        ].map(({ s, label, color }) => (
          <Card key={s}><CardContent className="pt-5">
            <div className={`text-[10px] uppercase tracking-[0.18em] text-${color}-700 font-semibold`}>{label}</div>
            <div className="text-3xl font-bold tabular-nums" data-testid={`kpi-${s.toLowerCase()}`}>{counts[s] || 0}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Tabs per tipe */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${
                active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="space-y-3" data-testid="my-changes-list">
        {loading && <div className="text-sm text-slate-500 py-6 text-center">Memuat...</div>}
        {!loading && items.length === 0 && (
          <div className="text-sm text-slate-500 p-8 text-center border border-dashed border-slate-300 rounded-md">
            Belum ada perubahan dengan kategori ini.
          </div>
        )}

        {/* PINDAH_TPS: kelompokkan berdasarkan MASUK / KELUAR relatif ke TPS PPDP */}
        {tab === "PINDAH_TPS" ? (
          <PindahTpsGroups items={items} myTps={user?.assigned_tps} canRevert={user?.role === "admin"} onReverted={load} />
        ) : (
          items.map((it) => (
            <ChangeCard key={it.id} it={it} tab={tab} />
          ))
        )}
      </div>
    </div>
  );
}

// ---------- PINDAH_TPS block ----------
function PindahTpsGroups({ items, myTps, canRevert, onReverted }) {
  const masuk = items.filter((it) => (it.data_baru?.tps) === myTps);
  const keluar = items.filter((it) => (it.data_lama?.tps) === myTps);
  const others = items.filter((it) => (it.data_baru?.tps) !== myTps && (it.data_lama?.tps) !== myTps);

  return (
    <div className="space-y-4">
      <PindahTpsSection title="Pemilih Masuk ke TPS Anda" icon={LogIn} color="emerald" list={masuk} myTps={myTps} kind="masuk" canRevert={canRevert} onReverted={onReverted} />
      <PindahTpsSection title="Pemilih Keluar dari TPS Anda" icon={LogOut} color="red" list={keluar} myTps={myTps} kind="keluar" canRevert={canRevert} onReverted={onReverted} />
      {others.length > 0 && (
        <PindahTpsSection title="Pindah TPS Lainnya" icon={ArrowRightLeft} color="slate" list={others} myTps={myTps} kind="other" canRevert={canRevert} onReverted={onReverted} />
      )}
    </div>
  );
}

function PindahTpsSection({ title, icon: Icon, color, list, myTps, kind, canRevert, onReverted }) {
  if (!list || list.length === 0) return null;
  return (
    <section className="space-y-2" data-testid={`pindah-section-${kind}`}>
      <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-${color}-700`}>
        <Icon className="h-4 w-4" /> {title} <span className="text-slate-500 font-mono normal-case tracking-normal">({list.length})</span>
      </div>
      {list.map((it) => (
        <PindahTpsCard key={it.id} it={it} myTps={myTps} kind={kind} canRevert={canRevert} onReverted={onReverted} />
      ))}
    </section>
  );
}

function PindahTpsCard({ it, myTps, kind, canRevert, onReverted }) {
  const [reverting, setReverting] = useState(false);
  const su = STATUS_UI[it.status] || STATUS_UI.APPLIED;
  const StatusIcon = su.icon;
  const dl = it.data_lama || {};
  const db = it.data_baru || {};
  const voter = it.voter_current || {};

  const doRevert = async () => {
    if (!confirm(`Batalkan pemindahan ${db.nama || voter.nama}? Voter akan dikembalikan ke ${dl.tps}.`)) return;
    setReverting(true);
    try {
      await api.post(`/pindah-tps/${it.id}/revert`);
      toast.success("Pindah TPS dibatalkan. Voter dikembalikan ke TPS asal.");
      onReverted?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReverting(false); }
  };

  const highlight = kind === "masuk" ? "text-emerald-700" : kind === "keluar" ? "text-red-700" : "text-slate-600";

  return (
    <article className="rounded-md border border-slate-200 bg-white p-4" data-testid={`pindah-${it.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-base">{db.nama || voter.nama || "Data pemilih"}</span>
            <span className={`inline-flex items-center gap-1 rounded-full ${su.color} px-2 py-0.5 text-[11px] font-semibold`}>
              <StatusIcon className="h-3 w-3" /> {su.label}
            </span>
          </div>
          <div className="text-xs font-mono text-slate-500 mt-0.5">{db.nik || voter.nik}</div>
          <div className={`text-xs mt-1 font-semibold ${highlight} flex items-center gap-1.5`}>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono">{dl.tps || "?"}</span>
            <ArrowRightLeft className="h-3.5 w-3.5" />
            <span className="rounded bg-slate-900 text-white px-1.5 py-0.5 font-mono">{db.tps || "?"}</span>
          </div>
        </div>
        <div className="text-right text-xs text-slate-500 shrink-0 space-y-0.5">
          <div>{it.submitted_at ? new Date(it.submitted_at).toLocaleString("id-ID") : ""}</div>
          <div className="text-slate-400">oleh {it.submitted_by_name || it.submitted_by_username}</div>
          {it.reverted_at && (
            <div className="text-red-700 mt-1">
              Dibatalkan {new Date(it.reverted_at).toLocaleString("id-ID")} oleh {it.reverted_by_username}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 text-xs bg-slate-50 rounded p-2.5 border border-slate-100 space-y-1">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-slate-500 uppercase text-[10px] tracking-wider mb-0.5">Sebelum</div>
            <div><b>{dl.tps}</b> · RT {dl.rt}/RW {dl.rw}</div>
            <div className="text-slate-600 truncate">{dl.dusun}</div>
            <div className="text-slate-600 truncate">{dl.alamat}</div>
          </div>
          <div>
            <div className="text-slate-500 uppercase text-[10px] tracking-wider mb-0.5">Setelah</div>
            <div><b>{db.tps}</b> · RT {db.rt}/RW {db.rw}</div>
            <div className="text-slate-600 truncate">{db.dusun}</div>
            <div className="text-slate-600 truncate">{db.alamat}</div>
          </div>
        </div>
        {it.alasan && (
          <div className="pt-1 border-t border-slate-200 text-slate-600">
            <span className="text-slate-500">Alasan:</span> {it.alasan}
          </div>
        )}
        {it.auto_cancelled_pending > 0 && (
          <div className="text-amber-800">
            {it.auto_cancelled_pending} pengajuan pending untuk voter ini otomatis dibatalkan.
          </div>
        )}
      </div>

      {canRevert && it.status === "APPLIED" && (
        <div className="mt-3 flex justify-end">
          <Button variant="outline" size="sm" onClick={doRevert} disabled={reverting} data-testid={`revert-pindah-${it.id}`}>
            <RotateCcw className="h-4 w-4 mr-1.5" />
            {reverting ? "Membatalkan..." : "Batalkan Pindah"}
          </Button>
        </div>
      )}
    </article>
  );
}

// Extract regular change card renderer to keep JSX clean
function ChangeCard({ it, tab }) {
  const su = STATUS_UI[it.status] || STATUS_UI.PENDING;
  const StatusIcon = su.icon;
  const voter = it.voter_current || it.voter_snapshot || {};
  const newData = it.data_baru || {};
  const buktiCount = (it.bukti_files || it.bukti_file_ids || []).length;
  // TMS badge: kalau UBAH/STATUS yang hanya ubah field status → status removal
  const isTMS = (it.tipe === "STATUS") ||
    (it.tipe === "UBAH" && newData.status && newData.status !== "ACTIVE" &&
      Object.keys(newData).filter((k) => k !== "status" && newData[k] !== null && newData[k] !== undefined && newData[k] !== "").length === 0);
  return (
    <article className="rounded-md border border-slate-200 bg-white p-4" data-testid={`change-${it.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-base">{voter.nama || newData.nama || "Data pemilih"}</span>
            <span className={`inline-flex items-center gap-1 rounded-full ${su.color} px-2 py-0.5 text-[11px] font-semibold`}>
              <StatusIcon className="h-3 w-3" /> {su.label}
            </span>
            {isTMS && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid="badge-tms">
                TMS
              </span>
            )}
            {buktiCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[11px] font-semibold" data-testid={`badge-bukti-${it.id}`}>
                <Paperclip className="h-3 w-3" /> {buktiCount} lampiran
              </span>
            )}
          </div>
          <div className="text-xs font-mono text-slate-500 mt-0.5">{voter.nik || newData.nik}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {voter.tps || newData.tps} · RT {voter.rt || newData.rt}/RW {voter.rw || newData.rw} · {voter.dusun || newData.dusun}
          </div>
        </div>
        <div className="text-right text-xs text-slate-500 shrink-0">
          <div>{it.submitted_at ? new Date(it.submitted_at).toLocaleString("id-ID") : ""}</div>
          {it.reverted_at && (
            <div className="text-red-700 mt-1">
              Dikembalikan {new Date(it.reverted_at).toLocaleString("id-ID")} oleh {it.reverted_by_username}
            </div>
          )}
        </div>
      </div>

      {/* Detail perubahan */}
      <div className="mt-3 text-xs bg-slate-50 rounded p-2.5 border border-slate-100">
        {it.tipe === "BARU" && (
          <div>
            <div className="text-slate-500 mb-1">Pemilih baru:</div>
            <div className="grid grid-cols-2 gap-1">
              <div><span className="text-slate-500">Nama:</span> <b>{newData.nama}</b></div>
              <div><span className="text-slate-500">NIK:</span> <span className="font-mono">{newData.nik}</span></div>
              <div><span className="text-slate-500">JK:</span> {newData.jenis_kelamin}</div>
              <div><span className="text-slate-500">Alamat:</span> {newData.alamat}</div>
            </div>
          </div>
        )}
        {(it.tipe === "STATUS" || it.tipe === "UBAH") && (
          <div>
            <div className="text-slate-500 mb-1">Perubahan:</div>
            {Object.entries(newData).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
              <div key={k} className="grid grid-cols-3 gap-2">
                <span className="text-slate-500">{k}:</span>
                <span className="line-through text-slate-400 truncate">
                  {k === "status" ? (STATUS_LABEL[it.voter_snapshot?.[k]] || it.voter_snapshot?.[k]) : it.voter_snapshot?.[k]}
                </span>
                <span className="font-semibold truncate">
                  {k === "status" ? (STATUS_LABEL[v] || v) : v}
                </span>
              </div>
            ))}
          </div>
        )}
        {it.alasan && (
          <div className="mt-2 pt-2 border-t border-slate-200 text-slate-600">
            <span className="text-slate-500">Alasan:</span> {it.alasan}
          </div>
        )}
        {it.revert_reason && (
          <div className="mt-1 text-red-700">
            <span className="text-slate-500">Catatan Panitia (Dikembalikan):</span> {it.revert_reason}
          </div>
        )}
        {it.status === "REJECTED" && it.catatan && (
          <div className="mt-1 text-red-700" data-testid={`catatan-tolak-${it.id}`}>
            <span className="text-slate-500">Alasan Ditolak Panitia:</span> {it.catatan}
          </div>
        )}
        {it.hold_reason && (
          <div className="mt-1 text-amber-800">
            <span className="text-slate-500">Catatan Panitia (Menunggu Verifikasi):</span> {it.hold_reason}
          </div>
        )}
        {it.status === "APPROVED" && it.catatan && (
          <div className="mt-1 text-emerald-700">
            <span className="text-slate-500">Catatan Panitia (Disetujui):</span> {it.catatan}
          </div>
        )}
      </div>
    </article>
  );
}
